import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';
import { env } from '@/lib/env';
import { CosmicLoader } from '@marcianus/design/components';
import { getPublicSettings, readString, type PublicSettings } from '@/services/settings';
import { getLoadingContext } from '@/services/loading';

// Injeta as cores white-label do CMS (branding.color_*) DIRETAMENTE nos tokens
// mds — a fonte visual canónica que TODO o CSS consome (.btn/.chip/.card/…).
// Assim a marca do tenant aplica-se em toda a app, não só num subconjunto legado.
// Cada cor primária/accent também alimenta os derivados (glow/borda/tint) por
// color-mix, para o white-label ser coerente com qualquer cor.
const TOKEN_MAP: Record<string, string[]> = {
  'branding.color_primary': ['--purple'],
  'branding.color_accent': ['--gold'],
  'branding.color_success': ['--success'],
  'branding.color_danger': ['--danger'],
  'branding.color_bg': ['--bg-0'],
};

function applyTokens(settings: PublicSettings) {
  // FOLHA DE ESTILO injetada, não `style` inline no <html>: um estilo inline
  // ganha a QUALQUER seletor — incluindo o `[data-theme="light"]` do theme.css —
  // e por isso o white-label colava o fundo escuro do tenant mesmo em tema
  // claro. Numa folha, a cascata volta a funcionar: o fundo do tenant só vale
  // fora do tema claro; as cores de marca valem nos dois.
  const geral: string[] = [];
  const soEscuro: string[] = [];
  // Cores do CMS IGUAIS ao canónico não geram override: emitir `--purple:#7B61FF`
  // por cima do tema claro esmagaria a recalibração (`#6B4FE8`, a versão que
  // aguenta texto branco). Só o que o tenant realmente mudou é que se impõe.
  const CANON: Record<string, string> = {
    'branding.color_primary': '#7b61ff',
    'branding.color_accent': '#ffd700',
    'branding.color_success': '#00c48c',
    'branding.color_danger': '#ff3b5c',
    'branding.color_bg': '#050508',
  };
  for (const [key, cssVars] of Object.entries(TOKEN_MAP)) {
    const value = readString(settings, key);
    if (!value) continue;
    if (CANON[key] && value.trim().toLowerCase() === CANON[key]) continue;
    // `color_bg` é a página do tenant — calibrada para o modo nativo (escuro).
    // Em claro, a página vem do theme.css; impor o fundo do tenant ali
    // devolveria exatamente o bug que este ficheiro existe para evitar.
    const alvo = key === 'branding.color_bg' ? soEscuro : geral;
    for (const cssVar of cssVars) alvo.push(`${cssVar}: ${value};`);
  }
  // Derivados do primário (mantêm o white-label coerente com qualquer cor).
  const primary = readString(settings, 'branding.color_primary');
  if (primary && primary.trim().toLowerCase() !== CANON['branding.color_primary']) {
    geral.push(
      `--purple-50: color-mix(in srgb, ${primary} 62%, white);`,
      `--purple-deep: color-mix(in srgb, ${primary} 72%, black);`,
      `--purple-20: color-mix(in srgb, ${primary} 20%, transparent);`,
      `--purple-10: color-mix(in srgb, ${primary} 10%, transparent);`,
      `--purple-glow: color-mix(in srgb, ${primary} 40%, transparent);`,
      `--border-accent: color-mix(in srgb, ${primary} 40%, transparent);`,
    );
  }
  const css = [
    geral.length ? `:root{${geral.join('')}}` : '',
    soEscuro.length ? `:root:not([data-theme="light"]){${soEscuro.join('')}}` : '',
  ].join('\n');
  let el = document.getElementById('brand-overrides') as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = 'brand-overrides';
    document.head.appendChild(el);
  }
  el.textContent = css;
  const name = readString(settings, 'branding.brand_name');
  // A marca fica publicada no <html> para a concha poder compor o título por
  // rota («Eventos · Marcianus») sem ficar acoplada às definições.
  // E COMPÕE em vez de esmagar: as definições chegam por REDE, portanto este
  // código corre DEPOIS de a concha já ter posto «Eventos · …». Um
  // `document.title = name` cego apagava a rota e o separador do browser
  // voltava a dizer o mesmo em todas as páginas — o problema a resolver.
  if (name) {
    document.documentElement.dataset.brand = name;
    const atual = document.title;
    document.title = atual.includes(' · ') ? `${atual.split(' · ')[0]} · ${name}` : name;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Bloqueia o primeiro render até termos a marca — evita "flash" de tema errado.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings', 'public'],
    queryFn: getPublicSettings,
    staleTime: 5 * 60_000,
  });

  // Passos REAIS do arranque (GET /loading/context), lado a lado com a marca.
  // Corre em PARALELO com as definições e nunca as bloqueia: se chegar primeiro,
  // o ecrã ganha conteúdo; se falhar, ninguém dá por isso. Sem `retry`, porque
  // um arranque não é sítio para insistir — ou está lá, ou a app já abriu.
  const { data: boot } = useQuery({
    queryKey: ['loading', 'boot'],
    queryFn: () => getLoadingContext('boot'),
    staleTime: 30_000,
    retry: false,
    enabled: isLoading,
  });

  useEffect(() => {
    if (data) applyTokens(data);
  }, [data]);

  // Preview: nunca bloqueia — usa o tema por defeito do MDS e aplica overrides se/quando chegarem.
  if (env.preview) return <>{children}</>;
  if (isLoading) return <CosmicLoader steps={boot?.steps ?? []} />;
  if (isError) return <div className="mx-boot">Não foi possível carregar a plataforma.</div>;
  return <>{children}</>;
}
